import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { locationService } from 'src/data/api/helios-service/locationService';
import { ApiRequestStatus, initApiStatus } from 'src/types/ApiRequestStatus';
import {
  ListLocationsRequest,
  Location,
  CreateLocationRequest,
  UpdateLocationRequest,
  GetLocationRequest,
  LocationStatus,
} from 'src/types/helios/Location';
import { handleError } from 'src/utils/generalUtils';

export interface LocationState {
  locations: Location[];
  selectedLocation: Location | null;
  fetchLocationsApiStatus: ApiRequestStatus;
  getLocationApiStatus: ApiRequestStatus;
  createLocationApiStatus: ApiRequestStatus;
  updateLocationApiStatus: ApiRequestStatus;
  nextToken?: string;
}

const initialState: LocationState = {
  locations: [],
  selectedLocation: null,
  fetchLocationsApiStatus: initApiStatus,
  getLocationApiStatus: initApiStatus,
  createLocationApiStatus: initApiStatus,
  updateLocationApiStatus: initApiStatus,
  nextToken: undefined,
};

export const fetchLocationsApiThunk = createAsyncThunk(
  'locations/fetchLocations',
  async (data: ListLocationsRequest, { rejectWithValue }) => {
    try {
      const response = await locationService.getAll(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const createLocationApiThunk = createAsyncThunk(
  'locations/createLocation',
  async (data: CreateLocationRequest, { rejectWithValue }) => {
    try {
      const response = await locationService.createLocation(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const updateLocationApiThunk = createAsyncThunk(
  'locations/updateLocation',
  async (data: UpdateLocationRequest, { rejectWithValue }) => {
    try {
      const response = await locationService.updateLocation(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const getLocationApiThunk = createAsyncThunk(
  'locations/getLocation',
  async (data: GetLocationRequest, { rejectWithValue }) => {
    try {
      const response = await locationService.getLocation(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

const locationsSlice = createSlice({
  name: 'locations',
  initialState,
  reducers: {
    resetAllApiStatus: (state) => {
      state.fetchLocationsApiStatus = initApiStatus;
      state.getLocationApiStatus = initApiStatus;
      state.createLocationApiStatus = initApiStatus;
      state.updateLocationApiStatus = initApiStatus;
    },

    addLocationToState: (state, action) => {
      state.locations.push(action.payload);
    },

    updateLocationInState: (state, action) => {
      const index = state.locations.findIndex(
        (location) => location.locationCode === action.payload.location.locationCode,
      );
      if (index !== -1) {
        state.locations[index] = action.payload.location;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Locations cases
      .addCase(fetchLocationsApiThunk.pending, (state) => {
        state.fetchLocationsApiStatus.isLoading = true;
        state.fetchLocationsApiStatus.errorMessage = null;
      })
      .addCase(fetchLocationsApiThunk.fulfilled, (state, action) => {
        state.fetchLocationsApiStatus.isLoading = false;
        state.fetchLocationsApiStatus.isSuccess = true;
        state.locations = action.payload.locations;
        state.nextToken = action.payload.nextToken;
      })
      .addCase(fetchLocationsApiThunk.rejected, (state, action) => {
        state.fetchLocationsApiStatus.isLoading = false;
        state.fetchLocationsApiStatus.isSuccess = false;
        state.fetchLocationsApiStatus.errorMessage = action.payload as string;
      });

    // Create Location cases
    builder
      .addCase(createLocationApiThunk.pending, (state) => {
        state.createLocationApiStatus.isLoading = true;
        state.createLocationApiStatus.errorMessage = null;
      })
      .addCase(createLocationApiThunk.fulfilled, (state, action) => {
        state.createLocationApiStatus.isLoading = false;
        state.createLocationApiStatus.isSuccess = true;
        // Add the newly created location to the locations list
        // CreateLocationResponse contains the location data directly, not wrapped in a location property
        const newLocation: Location = {
          locationCode: action.payload.locationCode,
          businessUnitId: action.payload.businessUnitId,
          isEnabledForBU: action.payload.isEnabledForBU,
          status: action.payload.isEnabledForBU
            ? LocationStatus.ACTIVE
            : LocationStatus.INACTIVE,
          region: action.payload.region,
          country: action.payload.country,
          state: action.payload.state,
          createdBy: action.payload.createdBy,
          createdAt: action.payload.createdAt,
          lastUpdatedAt: action.payload.lastUpdatedAt,
          lastUpdatedBy: action.payload.lastUpdatedBy,
          changeLog: action.payload.changeLog || [],
        };
        state.locations.unshift(newLocation);
      })
      .addCase(createLocationApiThunk.rejected, (state, action) => {
        state.createLocationApiStatus.isLoading = false;
        state.createLocationApiStatus.isSuccess = false;
        state.createLocationApiStatus.errorMessage = action.payload as string;
      });

    // Update Location cases
    builder
      .addCase(updateLocationApiThunk.pending, (state) => {
        state.updateLocationApiStatus.isLoading = true;
        state.updateLocationApiStatus.errorMessage = null;
        state.updateLocationApiStatus.successMessage = null;
      })
      .addCase(updateLocationApiThunk.fulfilled, (state, action) => {
        state.updateLocationApiStatus.isLoading = false;
        state.updateLocationApiStatus.isSuccess = true;
        locationsSlice.caseReducers.updateLocationInState(state, {
          type: 'updateLocationInState',
          payload: action.payload,
        });
      })
      .addCase(updateLocationApiThunk.rejected, (state, action) => {
        state.updateLocationApiStatus.isLoading = false;
        state.updateLocationApiStatus.isSuccess = false;
        state.updateLocationApiStatus.errorMessage = action.payload as string;
      });
    //Get Locations
    builder
      .addCase(getLocationApiThunk.pending, (state) => {
        state.getLocationApiStatus.isLoading = true;
        state.getLocationApiStatus.errorMessage = null;
      })
      .addCase(getLocationApiThunk.fulfilled, (state, action) => {
        state.getLocationApiStatus.isLoading = false;
        state.getLocationApiStatus.isSuccess = true;
        state.selectedLocation = action.payload.location;
      })
      .addCase(getLocationApiThunk.rejected, (state, action) => {
        state.getLocationApiStatus.isLoading = false;
        state.getLocationApiStatus.isSuccess = false;
        state.getLocationApiStatus.errorMessage = action.payload as string;
      });
  },
});

export const { resetAllApiStatus, updateLocationInState, addLocationToState } =
  locationsSlice.actions;
export default locationsSlice.reducer;
